import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { isCrmLocalPreview } from '../urls/api-shipments/crm';
import crmClient from './crm/crmClient';
import defaultMeta from './crm/defaultMeta';
import CompanyPipelineBoard from './crm/CompanyPipelineBoard';
import CompaniesView from './crm/CompaniesView';
import ContactsView from './crm/ContactsView';
import CallListView from './crm/CallListView';
import VisitRoutesView from './crm/VisitRoutesView';
import CampaignsView from './crm/CampaignsView';
import TemplatesView from './crm/TemplatesView';
import AIAssistantView from './crm/AIAssistantView';
import AIProspectingView from './crm/AIProspectingView';
import LiveChatView from './crm/LiveChatView';
import VisitorsView from './crm/VisitorsView';
import AbandonedCartsView from './crm/AbandonedCartsView';
import PipelineBoardView from './crm/PipelineBoardView';
import ImportView from './crm/ImportView';
import SocialView from './crm/SocialView';
import SeoView from './crm/SeoView';
import ReportsView from './crm/ReportsView';
import SubscriptionsView from './crm/SubscriptionsView';
import UserAccessView from './crm/UserAccessView';
import DispatchAlertsSettings from './crm/DispatchAlertsSettings';
import './CRMDashboard.css';

const CRMDashboard = () => {
  const { isDarkMode } = useTheme();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).has('visit_route') ? 'visit_routes' : 'pipeline');
  const [notif, setNotif] = useState({ items: [], unread_count: 0 });
  const [notifOpen, setNotifOpen] = useState(false);
  const [focusCompanyId, setFocusCompanyId] = useState(null);
  const [chatUnread, setChatUnread] = useState(0);
  const [smsUnread, setSmsUnread] = useState(0);
  const [hotLeads, setHotLeads] = useState(0);

  useEffect(() => {
    let active = true;
    crmClient
      .meta()
      .then(payload => {
        if (active) {
          setMeta(payload);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!active) {
          return;
        }
        if (isCrmLocalPreview()) {
          // Render the layout offline with a synthetic admin so every tab is visible.
          setMeta({ ...defaultMeta, current_user: { email: 'local preview', crm_role: 'ADMIN' } });
          setPreviewMode(true);
          setLoading(false);
        } else {
          setError('Unable to load CRM. Your access may have changed.');
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const isAdmin = Boolean(meta && meta.current_user && meta.current_user.crm_role === 'ADMIN');

  // Poll @mention notifications so tagged users see them without a refresh.
  useEffect(() => {
    if (!meta || previewMode) {
      return undefined;
    }
    let active = true;
    const fetchNotifs = () =>
      crmClient
        .listNotifications()
        .then(payload => {
          if (active && payload) {
            setNotif(payload);
          }
        })
        .catch(() => {});
    fetchNotifs();
    const timer = setInterval(fetchNotifs, 45000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [meta, previewMode]);

  // Poll live-chat unread so the Live Chat tab shows a badge for new website chats.
  useEffect(() => {
    if (!meta || previewMode) {
      return undefined;
    }
    let active = true;
    const fetchUnread = () => {
      crmClient
        .chatUnread('website')
        .then(payload => {
          if (active && payload) {
            setChatUnread(payload.unread || 0);
          }
        })
        .catch(() => {});
      crmClient
        .chatUnread('sms')
        .then(payload => {
          if (active && payload) {
            setSmsUnread(payload.unread || 0);
          }
        })
        .catch(() => {});
    };
    fetchUnread();
    const timer = setInterval(fetchUnread, 20000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [meta, previewMode]);

  // Poll hot website leads so the Visitors tab shows a badge.
  useEffect(() => {
    if (!meta || previewMode) {
      return undefined;
    }
    let active = true;
    const fetchHot = () =>
      crmClient
        .hotVisitorCount()
        .then(payload => {
          if (active && payload) {
            setHotLeads(payload.hot || 0);
          }
        })
        .catch(() => {});
    fetchHot();
    const timer = setInterval(fetchHot, 30000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [meta, previewMode]);

  const markAllNotificationsRead = () => {
    crmClient.markAllNotificationsRead().catch(() => {});
    setNotif(prev => ({ items: (prev.items || []).map(i => ({ ...i, is_read: true })), unread_count: 0 }));
  };

  const openNotification = notification => {
    if (!notification.is_read) {
      crmClient.markNotificationRead(notification.crm_notification_id).catch(() => {});
    }
    setNotif(prev => ({
      items: (prev.items || []).map(i =>
        i.crm_notification_id === notification.crm_notification_id ? { ...i, is_read: true } : i
      ),
      unread_count: Math.max(0, (prev.unread_count || 0) - (notification.is_read ? 0 : 1)),
    }));
    setNotifOpen(false);
    if (notification.crm_company_id) {
      setFocusCompanyId(notification.crm_company_id);
      setTab('companies');
    }
  };

  const tabs = useMemo(() => {
    const base = [
      { key: 'pipeline', label: 'Pipeline' },
      { key: 'companies', label: 'Companies' },
      { key: 'call_list', label: 'Call List' },
      { key: 'visit_routes', label: 'Visit Routes' },
      { key: 'abandoned_carts', label: 'Abandoned Carts' },
      { key: 'contacts', label: 'Contacts' },
      { key: 'investors', label: 'Investors' },
      { key: 'campaigns', label: 'Campaigns' },
      { key: 'templates', label: 'Templates' },
      { key: 'ai_assistant', label: 'AI Assistant' },
      { key: 'ai_prospecting', label: 'AI Prospecting' },
      { key: 'live_chat', label: 'Live Chat' },
      { key: 'sms_messages', label: 'SMS Messages' },
      { key: 'calls', label: 'Calls' },
      { key: 'email', label: 'Email' },
      { key: 'visitors', label: 'Visitors' },
      { key: 'social', label: 'Social' },
      { key: 'seo', label: 'Content' },
      { key: 'imports', label: 'Imports' },
      { key: 'reports', label: 'Reports' },
      { key: 'subscriptions', label: 'Subscriptions' }
    ];
    if (isAdmin) {
      base.push({ key: 'settings', label: 'Settings' });
    }
    return base;
  }, [isAdmin]);

  const openCompaniesForStage = () => setTab('companies');

  const themeStyle = {
    '--crm-surface': isDarkMode ? '#27293d' : '#ffffff',
    '--crm-surface-muted': isDarkMode ? '#202235' : '#f4f6f8',
    '--crm-page': isDarkMode ? '#1e1f2f' : '#eef1f4',
    '--crm-text': isDarkMode ? '#f7f8fa' : '#20242a',
    '--crm-muted': isDarkMode ? '#aeb3c2' : '#66707a',
    '--crm-border': isDarkMode ? '#3a3d55' : '#d9dee4',
    '--crm-accent': '#5e72e4'
  };

  return (
    <main className="content crm-page" style={themeStyle}>
      <header className="crm-header">
        <div>
          <div className="crm-eyebrow">Customer relationship workspace</div>
          <h1>CRM</h1>
          <p>Lifecycle pipeline, accounts, contacts, and salesperson call management.</p>
        </div>
        {meta && meta.current_user && (
          <div className="crm-header-actions">
            <div className="crm-notif">
              <button
                type="button"
                className="crm-notif-bell"
                onClick={() => setNotifOpen(open => !open)}
                aria-label="Notifications"
              >
                <span aria-hidden="true">🔔</span>
                {notif.unread_count > 0 && <span className="crm-notif-badge">{notif.unread_count}</span>}
              </button>
              {notifOpen && (
                <div className="crm-notif-menu">
                  <div className="crm-notif-head">
                    <span>Notifications</span>
                    {notif.unread_count > 0 && (
                      <button type="button" onClick={markAllNotificationsRead}>
                        Mark all read
                      </button>
                    )}
                  </div>
                  {(notif.items || []).length === 0 ? (
                    <div className="crm-notif-empty">No notifications yet.</div>
                  ) : (
                    <ul>
                      {notif.items.map(n => (
                        <li key={n.crm_notification_id} className={n.is_read ? '' : 'unread'}>
                          <button type="button" onClick={() => openNotification(n)}>
                            <span className="crm-notif-line">
                              <strong>{n.actor_email || 'A teammate'}</strong> tagged you at{' '}
                              <em>{n.company_name || 'an account'}</em>
                            </span>
                            {n.message && <span className="crm-notif-msg">{n.message}</span>}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <div className="crm-user-badge">
              <span>{meta.current_user.email || 'CRM user'}</span>
              <small>{meta.current_user.crm_role === 'ADMIN' ? 'CRM Admin' : 'CRM Sales User'}</small>
            </div>
          </div>
        )}
      </header>

      {previewMode && (
        <div className="crm-preview-banner">
          Local preview — the CRM backend isn’t connected, so data lists will be empty. Deploy api-shipments to load live accounts.
        </div>
      )}

      {loading && <div className="crm-empty">Loading CRM…</div>}
      {error && !loading && <div className="crm-empty crm-empty--error">{error}</div>}

      {!loading && !error && meta && (
        <>
          <nav className="crm-tabs" aria-label="CRM sections">
            {tabs.map(item => (
              <button
                type="button"
                key={item.key}
                className={tab === item.key ? 'active' : ''}
                onClick={() => setTab(item.key)}
              >
                {item.label}
                {item.key === 'live_chat' && chatUnread > 0 && <span className="crm-tab-badge">{chatUnread}</span>}
                {item.key === 'sms_messages' && smsUnread > 0 && <span className="crm-tab-badge">{smsUnread}</span>}
                {item.key === 'visitors' && hotLeads > 0 && <span className="crm-tab-badge">{hotLeads}</span>}
              </button>
            ))}
          </nav>

          <section className="crm-body">
            {tab === 'pipeline' && <CompanyPipelineBoard meta={meta} />}
            {tab === 'companies' && <CompaniesView meta={meta} initialStage="" focusCompanyId={focusCompanyId} />}
            {tab === 'call_list' && <CallListView meta={meta} onOpenCompanies={openCompaniesForStage} />}
            {tab === 'visit_routes' && <VisitRoutesView meta={meta} initialRouteId={new URLSearchParams(window.location.search).get('visit_route')} />}
            {tab === 'contacts' && <ContactsView meta={meta} />}
            {tab === 'investors' && (
              <PipelineBoardView
                pipeline="investor"
                meta={meta}
                title="Investor pipeline"
                subtitle="Track investor relationships from introduction through commitment."
                showAmount
                showOrganization
              />
            )}
            {tab === 'campaigns' && <CampaignsView meta={meta} />}
            {tab === 'templates' && <TemplatesView meta={meta} />}
            {tab === 'ai_assistant' && <AIAssistantView meta={meta} />}
            {tab === 'ai_prospecting' && <AIProspectingView meta={meta} />}
            {tab === 'live_chat' && <LiveChatView channel="website" />}
            {tab === 'sms_messages' && <LiveChatView channel="sms" title="SMS Messages" />}
            {tab === 'calls' && <LiveChatView channel="voice" title="Calls" />}
            {tab === 'email' && <LiveChatView channel="email" title="Email" />}
            {tab === 'visitors' && <VisitorsView />}
            {tab === 'social' && <SocialView meta={meta} isAdmin={isAdmin} />}
            {tab === 'seo' && <SeoView meta={meta} isAdmin={isAdmin} />}
            {tab === 'abandoned_carts' && <AbandonedCartsView />}
            {tab === 'imports' && <ImportView meta={meta} />}
            {tab === 'reports' && <ReportsView />}
            {tab === 'subscriptions' && <SubscriptionsView />}
            {tab === 'settings' && isAdmin && <UserAccessView meta={meta} />}
            {tab === 'settings' && isAdmin && <DispatchAlertsSettings />}
          </section>
        </>
      )}
    </main>
  );
};

export default CRMDashboard;
